import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star } from 'lucide-react';
import { Testimonial } from '@/hooks/useTestimonials';

const testimonialSchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  country: z.string().min(2, 'Le pays doit contenir au moins 2 caractères'),
  date: z.string().min(1, 'La date est requise'),
  rating: z.number().min(1).max(5),
  comment: z.string().min(10, 'Le commentaire doit contenir au moins 10 caractères'),
  visible: z.boolean(),
});

type TestimonialFormData = z.infer<typeof testimonialSchema>;

interface TestimonialFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TestimonialFormData) => Promise<{ success: boolean }>;
  testimonial?: Testimonial;
  title: string;
}

export const TestimonialForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  testimonial, 
  title 
}: TestimonialFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRating, setSelectedRating] = useState(testimonial?.rating || 5);

  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm<TestimonialFormData>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: {
      name: testimonial?.name || '',
      country: testimonial?.country || '',
      date: testimonial?.date || '',
      rating: testimonial?.rating || 5,
      comment: testimonial?.comment || '',
      visible: testimonial?.visible ?? true,
    },
  });

  useEffect(() => {
    if (testimonial) {
      reset({
        name: testimonial.name,
        country: testimonial.country,
        date: testimonial.date,
        rating: testimonial.rating,
        comment: testimonial.comment,
        visible: testimonial.visible,
      });
      setSelectedRating(testimonial.rating);
    }
  }, [testimonial, reset]);

  const handleFormSubmit = async (data: TestimonialFormData) => {
    setIsSubmitting(true);
    const result = await onSubmit(data);
    setIsSubmitting(false);
    
    if (result.success) {
      reset();
      onClose();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-6 w-6 cursor-pointer transition-colors ${
          i < selectedRating ? 'fill-roca-yellow text-roca-yellow' : 'text-muted-foreground hover:text-roca-yellow'
        }`}
        onClick={() => {
          setSelectedRating(i + 1);
          setValue('rating', i + 1);
        }}
      />
    ));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="Nom du voyageur"
              />
              {errors.name && (
                <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="country">Pays *</Label>
              <Input
                id="country"
                {...register('country')}
                placeholder="France"
              />
              {errors.country && (
                <p className="text-sm text-destructive mt-1">{errors.country.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              {...register('date')}
              placeholder="Mars 2024"
            />
            {errors.date && (
              <p className="text-sm text-destructive mt-1">{errors.date.message}</p>
            )}
          </div>

          <div>
            <Label>Note *</Label>
            <div className="flex gap-1 mt-2">
              {renderStars()}
            </div>
            <input type="hidden" {...register('rating', { valueAsNumber: true })} />
          </div>

          <div>
            <Label htmlFor="comment">Commentaire *</Label>
            <Textarea
              id="comment"
              {...register('comment')}
              placeholder="Partagez votre expérience avec Roca Rio..."
              className="min-h-[100px]"
            />
            {errors.comment && (
              <p className="text-sm text-destructive mt-1">{errors.comment.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="visible"
              {...register('visible')}
              className="rounded"
            />
            <Label htmlFor="visible">Visible sur le site</Label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};